<template>
  <UiBaseFormVee
    submitOnCtrlEnter
    :errorText="errorText"
    :resetCallback="formResetCallback"
    :onSubmit="onSubmit">
    <UiInputTextFieldVee
      name="firstName"
      :label="$t('form.label.first-name')"
      :placeholder="$t('form.placeholder.first-name')"
      :rules="['required']"
      isRequired
      v-model="form.firstName"
      @update:model-value="resetValidationMessages" />

    <UiInputTextFieldVee
      name="lastName"
      :label="$t('form.label.last-name')"
      :placeholder="$t('form.placeholder.last-name')"
      :rules="['required']"
      isRequired
      v-model="form.lastName"
      @update:model-value="resetValidationMessages" />

    <UiInputTextFieldVee
      name="email"
      :label="$t('form.label.email')"
      :placeholder="$t('form.placeholder.email')"
      :rules="['required', 'email']"
      isRequired
      type="email"
      v-model="form.email"
      @update:model-value="resetValidationMessages">
      <template v-if="EMU.isEmulatingFirestore" #trailingIcon>
        <button
          type="button"
          class="pointer-events-auto flex h-5 w-5 cursor-pointer items-center justify-center opacity-50 hover:opacity-100"
          v-tooltip="$t('W: Indsæt dummy-email')"
          @click="insertFakeEmail">
          <IconLucideSparkles class="h-5 w-5" />
        </button>
      </template>
    </UiInputTextFieldVee>

    <UiComboboxVee
      name="currencyCode"
      :label="$t('term.currency')"
      :rules="['required']"
      isRequired
      v-model="form.currencyCode"
      :options="currencyOptions" />

    <UiInputSelectVee
      name="pricingSegmentID"
      :label="$t('area.customer-account.field.pricing-segment')"
      :rules="['required']"
      isRequired
      v-model="form.pricingSegmentID"
      :options="segmentOptions" />

    <UiInputPhoneVee
      name="phone"
      :label="$t('form.label.phone')"
      v-model="form.phone"
      @update:model-value="resetValidationMessages" />

    <template #buttons>
      <UiBaseButton
        :text="$t('action.create')"
        :isLoading="isLoading"
        type="submit" />
    </template>
  </UiBaseFormVee>
</template>

<script lang="ts">
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import { useAppStore } from '@/stores/appStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useAccountStore } from '@/stores/accountStore'
import { getErrorMessage } from '@/globals/typescript/utils/errorMessages'
import { generateFakeEmail } from '@/globals/typescript/utils/fakeEmail'
import { EMU } from '@/globals/typescript/utils/host'
import { useToast } from '@/globals/typescript/utils/toast'
import type { TLabManagementCurrencyCode } from '@team-milva/lab-models/currency'
import { mapActions, mapState } from 'pinia'
import type { PropType } from 'vue'

type TCreatePrivateCustomerUserForm = {
  firstName: string
  lastName: string
  email: string
  phone: { number: number; iso2: string }
  currencyCode: TLabManagementCurrencyCode | ''
  pricingSegmentID: string
}

function createForm(): TCreatePrivateCustomerUserForm {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: { number: 0, iso2: 'dk' },
    currencyCode: '',
    pricingSegmentID: '',
  }
}

export default {
  name: 'CreateCustomerUserPrivateForm',

  props: {
    onSuccess: {
      type: Function as PropType<(id: string) => void>,
      required: false,
      default: undefined,
    },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: createForm(),
      errorText: '',
      isLoading: false,
      EMU,
    }
  },

  computed: {
    ...mapState(usePricingSegmentStore, ['allPricingSegments']),
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(useAccountStore, ['availableCurrencies', 'defaultCurrencyCode']),

    segmentOptions() {
      return this.allPricingSegments
        .filter(
          (x) =>
            x.activeState === 'active' &&
            x.currencyCode === this.form.currencyCode
        )
        .map((pricingSegment) => ({
          value: pricingSegment.id,
          text: pricingSegment.getLabel(this.currentLocale),
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    currencyOptions(): { value: TLabManagementCurrencyCode; text: string }[] {
      return this.availableCurrencies.map((currency) => ({
        value: currency.code as TLabManagementCurrencyCode,
        text: `${currency.name[this.currentLocale] || currency.name['en']} (${currency.code})`,
      }))
    },
  },

  watch: {
    'form.currencyCode'(newVal: TLabManagementCurrencyCode) {
      const currentSegment = this.allPricingSegments.find(
        (x) => x.id === this.form.pricingSegmentID
      )
      if (currentSegment && currentSegment.currencyCode !== newVal) {
        this.form.pricingSegmentID = ''
      }
      this.resetValidationMessages()
    },
    'form.pricingSegmentID'() {
      this.resetValidationMessages()
    },
  },

  methods: {
    ...mapActions(useCustomerUserStore, ['createCustomerUserPrivate']),
    ...mapActions(useModalStore, ['closeTop']),

    insertFakeEmail() {
      this.form.email = generateFakeEmail()
      this.resetValidationMessages()
    },

    formResetCallback(): boolean {
      if (this.errorText) {
        return false
      }

      this.form = createForm()
      this.form.currencyCode = this.defaultCurrencyCode
      return true
    },

    resetValidationMessages() {
      this.errorText = ''
    },

    async onSubmit() {
      this.isLoading = true
      this.errorText = ''

      try {
        const fullName = `${this.form.firstName} ${this.form.lastName}`

        const id = await this.createCustomerUserPrivate({
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          email: this.form.email,
          phone: this.form.phone.number > 0 ? this.form.phone : undefined,
          currencyCode: this.form.currencyCode as TLabManagementCurrencyCode,
          pricingSegmentID: this.form.pricingSegmentID,
        })

        this.unsavedGuard.takeSnapshot()
        useToast().success(
          this.$t('area.customer-account.private-customers.create.success', {
            name: fullName,
          })
        )
        this.closeTop()
        this.onSuccess?.(id)
      } catch (err) {
        if (err instanceof Error && err.message === 'already-exists') {
          this.errorText = getErrorMessage('auth/email-already-exists')
        } else {
          this.errorText =
            err instanceof Error
              ? getErrorMessage(err.message)
              : this.$t('error.common.standard')
          useToast().error(this.errorText)
        }
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    this.form.currencyCode = this.defaultCurrencyCode

    // Registered per child form rather than once on CreateCustomerModal, so each
    // form's own `form` object is its own snapshot — the same shape every other
    // guarded surface uses. Only one child is mounted at a time, so the modal's
    // own id is enough for ModalHost and for the pill switch to consult.
    //
    // Must come after the currencyCode seed above, or the baseline holds an empty
    // currency and the form reads dirty the moment it opens.
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
